/**
 * Configuration settings for the application
 * Centralizes environment variable access
 */

// Get environment variables with fallbacks for optional variables
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return process.env[key] || defaultValue;
};

// Get required environment variables - throw error if missing
const getRequiredEnvVar = (key: string): string => {
  const value = process.env[key];
  if (!value) {
    throw new Error(`Required environment variable ${key} is not set`);
  }
  return value;
};

// Revalidation time (in seconds)
// 7 days = 604800 seconds
export const REVALIDATE_TIME = 604800;

// Base URL for the site - must be set via environment variable
const BASE_URL = (() => {
  const envBaseUrl = process.env.NEXT_PUBLIC_BASE_URL;
  
  if (!envBaseUrl) {
    throw new Error(
      'Required environment variable NEXT_PUBLIC_BASE_URL is not set. ' +
      'Please set NEXT_PUBLIC_BASE_URL in your .env.local file or environment variables. ' +
      'Example: NEXT_PUBLIC_BASE_URL=http://noproduct.local:3000'
    );
  }
  
  // Ensure no trailing slash
  return envBaseUrl.endsWith('/') ? envBaseUrl.slice(0, -1) : envBaseUrl;
})();

// Export BASE_URL for direct use
export { BASE_URL };

// Site configuration settings
const siteConfig = {
  // Storyblok configuration
  storyblok: {
    spaceId: getEnvVar('STORYBLOK_SPACE_ID'),
    apiKey: getEnvVar('STORYBLOK_API_KEY'),
    region: getEnvVar('STORYBLOK_SERVER_LOCATION', 'eu').toLowerCase() === 'eu' ? 'eu' : 'us',
    version: 'published', // Always use published content only
  },
  
  // Environment detection
  environment: {
    isProduction: process.env.NODE_ENV === 'production',
    isDevelopment: process.env.NODE_ENV === 'development',
  },
  
  // Organization/Company Details (for schema.org and general site info)
  organization: {
    // Basic company information
    name: 'MusicMagic.io',
    legalName: 'MusicMagic.io',
    url: BASE_URL,
    email: 'hello@musicmagic.io',
    
    // Founder/Leadership information
    founder: {
      name: 'Nicholas Oliver',
      jobTitle: 'Founder',
      email: 'hello@musicmagic.io',
      bio: 'Founder of MusicMagic.io, curating the best Spotify playlists from Instagram Stories.',
      url: BASE_URL,
      sameAs: [
        'https://www.linkedin.com/in/nicholasoliver/',
        'https://twitter.com/nicholasoliver'
      ]
    },
    
    // Company founding and legal information
    foundingDate: '2020-01-01T00:00:00+00:00',
    
    // Social media and web presence
    sameAs: [
      'https://instagram.com/musicmagicio',
      'https://twitter.com/musicmagicio',
      'https://www.facebook.com/musicmagicio',
      'https://www.linkedin.com/company/musicmagic-io/'
    ],
    
    // Brand assets
    logo: {
      url: `${BASE_URL}/assets/img/musicmagic-io-main-logo.png`,
      width: 512,
      height: 512,
      alt: 'MusicMagic.io Logo'
    },
    
    // Contact information for forms/mail
    contact: {
      email: 'hello@musicmagic.io',
      supportEmail: 'hello@musicmagic.io',
      fromEmail: 'noreply@musicmagic.io',
      domainName: 'musicmagic.io'
    },
    
    // Schema.org IDs for structured data
    schemaIds: {
      organization: `${BASE_URL}/#organization`,
      website: `${BASE_URL}/#website`,
      logo: `${BASE_URL}/#logo`
    }
  },
  
  // Site configuration
  site: {
    name: 'MusicMagic.io',
    url: BASE_URL,
    domain: new URL(BASE_URL).hostname,
    locale: 'en',
    title: "MusicMagic.io - Discover the Best Spotify Playlists",
    companyName: "MusicMagic.io",
    defaultDescription: "Explore the best Spotify playlists crafted from Instagram Stories of top musicians, DJs, influencers, festivals, and events for an unmatched music vibe.",
    keywords: "spotify playlists, music discovery, instagram stories, curated playlists, music curation",
    social: {
      twitter: "@musicmagicio",
      instagram: "@musicmagicio"
    }
  },
  
  // Assets configuration
  assets: {
    images: {
      defaultOgImage: `${BASE_URL}/assets/img/musicmagic-spotify-playlists.jpg`,
      defaultTwitterImage: `${BASE_URL}/assets/img/musicmagic-spotify-playlists-twitter.jpg`
    }
  },
  
  // Pagination defaults
  pagination: {
    defaultPerPage: 9
  },
  
  // Content defaults
  content: {
    // Default date format for blog posts
    defaultDateFormat: {
      year: 'numeric' as const,
      month: 'long' as const, 
      day: 'numeric' as const
    },
    // Default values for missing content (should be empty strings, not fallbacks)
    defaults: {
      author: '',
      category: 'General',
      excerpt: '',
      date: ''
    }
  }
};

export default siteConfig; 